/**
 * Enhanced stream eligibility checker with comprehensive fraud prevention
 * This replaces the basic eligibility checker with advanced fraud detection
 */

import { validateNotSelfStreaming } from "../streamSelfCheckValidator";
import { validateTrackSponsorship } from "../trackSponsorshipValidator";
import { validateActiveSponsorshipRelationship } from "../sponsorshipRelationshipValidator";
import { validateSponsorWalletBalance } from "../sponsorWalletValidator";
import { CURRENCY_CONSTANTS, formatNaira } from "../currencyUtils";
import { detectStreamFraud, StreamContext } from "./fraudDetectionService";
import {
  validateDuplicateStream,
  checkVelocityLimits,
} from "./duplicateStreamValidator";
import { getUserIPAddress } from "../geoIpService";
import { isCurrentUserUnlimitedStream } from "../streamWhitelist";

export interface EnhancedEligibilityResult {
  eligible: boolean;
  sponsorId?: string;
  reason?: string;
  fraudDetection?: {
    riskLevel: "low" | "medium" | "high" | "critical";
    fraudReasons: string[];
    deviceTrustScore: number;
    recommendation: "allow" | "throttle" | "reject" | "block";
  };
  duplicateCheck?: {
    isDuplicate: boolean;
    allowanceType: "none" | "sponsored_only" | "full";
  };
  velocityCheck?: {
    exceeded: boolean;
    streamsInLastHour: number;
    streamsInLastDay: number;
    maxAllowedPerHour: number;
    maxAllowedPerDay: number;
  };
}

/**
 * Enhanced eligibility check with comprehensive fraud prevention
 */
export async function isEligibleForSponsoredPaymentEnhanced(
  trackId: string,
  userId: string,
  artistId: string,
  userAgent?: string,
): Promise<EnhancedEligibilityResult> {
  try {
    console.log(
      "🔍 [ENHANCED_ELIGIBILITY] Starting comprehensive eligibility check:",
      {
        trackId,
        userId,
        artistId,
        stream_payment_amount: formatNaira(
          CURRENCY_CONSTANTS.STREAM_PAYMENT_NAIRA,
        ),
        timestamp: new Date().toISOString(),
      },
    );

    // 1. Basic validation checks first
    const selfStreamCheck = validateNotSelfStreaming(userId, artistId);
    if (!selfStreamCheck.valid) {
      console.log("❌ [ENHANCED_ELIGIBILITY] Failed self-streaming check:", {
        reason: selfStreamCheck.reason,
        userId,
        artistId,
      });
      return { eligible: false, reason: selfStreamCheck.reason };
    }

    // 2. Verify the track is sponsored and get track details
    const trackValidation = await validateTrackSponsorship(trackId, artistId);
    if (!trackValidation.valid) {
      console.log(
        "❌ [ENHANCED_ELIGIBILITY] Failed track sponsorship validation:",
        {
          reason: trackValidation.reason,
          trackId,
          artistId,
        },
      );
      return { eligible: false, reason: trackValidation.reason };
    }

    const track = trackValidation.track!;
    console.log("✅ [ENHANCED_ELIGIBILITY] Track validation passed:", {
      trackId: track.id,
      trackTitle: track.title,
      sponsorId: track.sponsor_id,
      isSponsored: track.is_sponsored,
    });

    // 3. NEW: Comprehensive fraud detection
    const fraudDetectionResult = await detectStreamFraud({
      userId,
      trackId,
      artistId,
      userAgent,
    });

    console.log("🛡️ [ENHANCED_ELIGIBILITY] Fraud detection result:", {
      allowed: fraudDetectionResult.allowed,
      riskLevel: fraudDetectionResult.riskLevel,
      recommendation: fraudDetectionResult.recommendation,
      fraudReasons: fraudDetectionResult.fraudReasons,
    });

    // RELAXED: Only block for critical/block recommendations, allow medium risk
    if (fraudDetectionResult.recommendation === "block") {
      return {
        eligible: false,
        reason: `Stream blocked due to critical fraud risk: ${fraudDetectionResult.fraudReasons.join("; ")}`,
        fraudDetection: {
          riskLevel: fraudDetectionResult.riskLevel,
          fraudReasons: fraudDetectionResult.fraudReasons,
          deviceTrustScore: fraudDetectionResult.deviceTrustScore,
          recommendation: fraudDetectionResult.recommendation,
        },
      };
    }

    // RELAXED: Allow medium and high risk streams (only block critical)
    console.log(
      "✅ [ENHANCED_ELIGIBILITY] Fraud check passed with relaxed policy:",
      {
        riskLevel: fraudDetectionResult.riskLevel,
        recommendation: fraudDetectionResult.recommendation,
        deviceTrustScore: fraudDetectionResult.deviceTrustScore,
      },
    );

    // 4. NEW: Enhanced duplicate stream protection with IP-based validation
    const whitelistedUnlimited = await isCurrentUserUnlimitedStream();
    const userIP = await getUserIPAddress();
    const duplicateCheck = whitelistedUnlimited
      ? { isDuplicate: false, allowanceType: "full" as const }
      : await validateDuplicateStream(
          trackId,
          userId,
          fraudDetectionResult.deviceFingerprint,
          userIP || undefined,
        );

    console.log("🔄 [ENHANCED_ELIGIBILITY] IP-based duplicate stream check:", {
      ...duplicateCheck,
      userIP: userIP || "unknown",
      whitelistedUnlimited,
    });

    if (duplicateCheck.isDuplicate && duplicateCheck.allowanceType === "none") {
      return {
        eligible: false,
        reason: (duplicateCheck as any).reason || "Duplicate stream detected",
        duplicateCheck: {
          isDuplicate: true,
          allowanceType: duplicateCheck.allowanceType,
        },
        fraudDetection: {
          riskLevel: fraudDetectionResult.riskLevel,
          fraudReasons: fraudDetectionResult.fraudReasons,
          deviceTrustScore: fraudDetectionResult.deviceTrustScore,
          recommendation: fraudDetectionResult.recommendation,
        },
      };
    }

    // 5. NEW: Velocity abuse protection (skipped for whitelisted users)
    const velocityCheck = whitelistedUnlimited
      ? {
          exceeded: false,
          reason: undefined as string | undefined,
          streamsInLastHour: 0,
          streamsInLastDay: 0,
          maxAllowedPerHour: Number.MAX_SAFE_INTEGER,
          maxAllowedPerDay: Number.MAX_SAFE_INTEGER,
        }
      : await checkVelocityLimits(userId);
    console.log("🚀 [ENHANCED_ELIGIBILITY] Velocity check:", velocityCheck);

    if (velocityCheck.exceeded) {
      return {
        eligible: false,
        reason: velocityCheck.reason || "Too many streams in short period",
        velocityCheck: {
          exceeded: true,
          streamsInLastHour: velocityCheck.streamsInLastHour,
          streamsInLastDay: velocityCheck.streamsInLastDay,
          maxAllowedPerHour: velocityCheck.maxAllowedPerHour,
          maxAllowedPerDay: velocityCheck.maxAllowedPerDay,
        },
        fraudDetection: {
          riskLevel: fraudDetectionResult.riskLevel,
          fraudReasons: fraudDetectionResult.fraudReasons,
          deviceTrustScore: fraudDetectionResult.deviceTrustScore,
          recommendation: fraudDetectionResult.recommendation,
        },
      };
    }

    // 6. Check for active sponsorship relationship
    console.log(
      "🤝 [ENHANCED_ELIGIBILITY] Checking sponsorship relationship:",
      {
        artistId,
        sponsorId: track.sponsor_id,
      },
    );

    const sponsorshipValidation = await validateActiveSponsorshipRelationship(
      artistId,
      track.sponsor_id,
    );
    if (!sponsorshipValidation.valid) {
      console.error(
        "❌ [ENHANCED_ELIGIBILITY] Failed sponsorship relationship validation:",
        {
          reason: sponsorshipValidation.reason,
          artistId,
          sponsorId: track.sponsor_id,
          trackId,
          trackTitle: track.title,
        },
      );
      return {
        eligible: false,
        reason: sponsorshipValidation.reason,
        fraudDetection: {
          riskLevel: fraudDetectionResult.riskLevel,
          fraudReasons: fraudDetectionResult.fraudReasons,
          deviceTrustScore: fraudDetectionResult.deviceTrustScore,
          recommendation: fraudDetectionResult.recommendation,
        },
      };
    }

    console.log(
      "✅ [ENHANCED_ELIGIBILITY] Sponsorship relationship validation passed",
    );

    // 7. Check sponsor wallet balance
    console.log("💰 [ENHANCED_ELIGIBILITY] Checking sponsor wallet balance:", {
      sponsorId: track.sponsor_id,
      requiredAmount: CURRENCY_CONSTANTS.STREAM_PAYMENT_NAIRA,
    });

    const walletValidation = await validateSponsorWalletBalance(
      track.sponsor_id,
      CURRENCY_CONSTANTS.STREAM_PAYMENT_NAIRA,
    );
    if (!walletValidation.valid) {
      console.error(
        "❌ [ENHANCED_ELIGIBILITY] Failed wallet balance validation:",
        {
          reason: walletValidation.reason,
          sponsorId: track.sponsor_id,
          balance: walletValidation.balance,
          requiredAmount: CURRENCY_CONSTANTS.STREAM_PAYMENT_NAIRA,
        },
      );
      return {
        eligible: false,
        reason: walletValidation.reason,
        fraudDetection: {
          riskLevel: fraudDetectionResult.riskLevel,
          fraudReasons: fraudDetectionResult.fraudReasons,
          deviceTrustScore: fraudDetectionResult.deviceTrustScore,
          recommendation: fraudDetectionResult.recommendation,
        },
      };
    }

    // All checks passed!
    console.log(
      "✅ [ENHANCED_ELIGIBILITY] Stream is eligible for sponsored payment with enhanced fraud protection:",
      {
        trackId,
        trackTitle: track.title,
        artistId,
        sponsorId: track.sponsor_id,
        sponsor_balance_naira: walletValidation.balance,
        sponsor_balance_formatted: walletValidation.balance
          ? formatNaira(walletValidation.balance)
          : "Unknown",
        stream_payment_amount: formatNaira(
          CURRENCY_CONSTANTS.STREAM_PAYMENT_NAIRA,
        ),
        fraudRiskLevel: fraudDetectionResult.riskLevel,
        deviceTrustScore: fraudDetectionResult.deviceTrustScore,
        duplicateCheckPassed: !duplicateCheck.isDuplicate,
        velocityCheckPassed: !velocityCheck.exceeded,
        timestamp: new Date().toISOString(),
      },
    );

    return {
      eligible: true,
      sponsorId: track.sponsor_id,
      fraudDetection: {
        riskLevel: fraudDetectionResult.riskLevel,
        fraudReasons: fraudDetectionResult.fraudReasons,
        deviceTrustScore: fraudDetectionResult.deviceTrustScore,
        recommendation: fraudDetectionResult.recommendation,
      },
      duplicateCheck: {
        isDuplicate: duplicateCheck.isDuplicate,
        allowanceType: duplicateCheck.allowanceType,
      },
      velocityCheck: {
        exceeded: velocityCheck.exceeded,
        streamsInLastHour: velocityCheck.streamsInLastHour,
        streamsInLastDay: velocityCheck.streamsInLastDay,
        maxAllowedPerHour: velocityCheck.maxAllowedPerHour,
        maxAllowedPerDay: velocityCheck.maxAllowedPerDay,
      },
    };
  } catch (error) {
    console.error(
      "🔥 [ENHANCED_ELIGIBILITY] Exception in enhanced eligibility check:",
      {
        error,
        errorMessage: error instanceof Error ? error.message : "Unknown error",
        trackId,
        userId,
        artistId,
        timestamp: new Date().toISOString(),
      },
    );
    return {
      eligible: false,
      reason: "System error during enhanced eligibility check",
    };
  }
}
